import React, { useMemo } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { Checkbox, Popup, Typography } from '_atoms';
import type { TableColumnType } from '_atoms/Table/types';
import './style.scss';

interface PopupConfigureTableProps {
  open: boolean;
  columns: TableColumnType[];
  tableName?: string;
  shownColumns?: Record<string, boolean>;
  onSave: (columns: Record<string, boolean>) => void;
  onClose: () => void;
}

export const PopupConfigureTable: React.FC<PopupConfigureTableProps> = ({
  open,
  onSave,
  onClose,
  columns,
  tableName,
  shownColumns,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form) as Record<string, boolean> | undefined;

  const allFalse = values ? Object.values(values).every(val => val === false || val === undefined) : true;

  const hasFixed = useMemo(() => columns.find(c => c.fixed), [columns]);

  const onClosePopup = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = () => {
    const tableConfig = form.getFieldsValue();
    Object.keys(tableConfig).forEach(key => {
      tableConfig[key] = !!tableConfig[key];
    });
    onSave(tableConfig);
    sessionStorage.setItem(tableName as string, JSON.stringify(tableConfig));
    onClose();
  };

  return (
    <Popup
      className="popup-configure-table"
      open={open}
      title={t('translation:popup_configure_table.title')}
      cancelButtonProps={{ onClick: onClosePopup, children: t('translation:popup_configure_table.button_cancel') }}
      okButtonProps={{
        onClick: onFinish,
        children: t('translation:popup_configure_table.button_apply'),
        disabled: allFalse,
      }}
    >
      <Form form={form} initialValues={shownColumns}>
        {hasFixed && (
          <>
            <Typography.Label>{t('translation:popup_configure_table.label_pinned')}</Typography.Label>
            {columns.map(
              c =>
                c.fixed && (
                  <Checkbox
                    label={typeof c.title === 'function' ? (c.title as () => React.ReactNode)() : c.title}
                    disabled
                    itemProps={{ name: c.key }}
                  />
                )
            )}
            <Typography.Label style={{ marginTop: 12 }}>
              {t('translation:popup_configure_table.label_scrolling')}
            </Typography.Label>
          </>
        )}
        {columns.map(
          c =>
            !c.fixed && (
              <Checkbox
                label={typeof c.title === 'function' ? (c.title as () => React.ReactNode)() : c.title}
                itemProps={{ name: c.key }}
              />
            )
        )}
      </Form>
    </Popup>
  );
};
